import React from 'react';
import styled from 'styled-components';
import { SectionShell, SubHeading, EditButton, Grid, Cell, HeadCell } from './HowToRead';

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  div {
    background: ${({ theme }) => theme.colors.report.panel};
    border-top: 3px solid ${({ theme }) => theme.colors.report.brand};
    border-radius: 6px;
    padding: 12px 14px;
    font-size: 13px;
  }
  b { display: block; color: ${({ theme }) => theme.colors.report.noteTitle}; }
  strong { display: block; margin: 4px 0; color: ${({ theme }) => theme.colors.report.brand}; }
`;

const Notes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  column-gap: 32px;
  div { padding: 10px 0; border-bottom: 1px solid ${({ theme }) => theme.colors.report.grayLight}; font-size: 13px; }
  span { display: block; font-size: 12px; font-weight: 700; color: ${({ theme }) => theme.colors.report.muted}; margin-bottom: 4px; }
`;

export function methodNotes(data, narrative, t) {
  const m = data.method;
  const c = data.survey.questionCounts || {};
  const notes = [
    [t('survey.report.methodScale'), t('survey.report.methodScaleValue', { min: m.scaleMin, max: m.scaleMax })],
    [t('survey.report.methodFavorable'), t('survey.report.methodFavorableValue', { min: m.favorableMin })],
    [t('survey.report.methodStructure'), t('survey.report.methodStructureValue', {
      total: (c.scored || 0) + (c.freeText || 0) + (c.breakdown || 0) + (c.other || 0),
      breakdown: c.breakdown || 0, scored: c.scored || 0, freeText: c.freeText || 0,
      dimensions: data.survey.dimensionCount, subDimensions: data.survey.subDimensionCount,
    })],
    [t('survey.report.methodAnonymity'), t('survey.report.methodAnonymityValue', { n: data.threshold })],
    [t('survey.report.methodApplication'), t('survey.report.methodApplicationValue')],
  ];
  if ((m.eligibility || []).length > 0) {
    notes.push([t('survey.report.methodEligibility'),
      m.eligibility.map((e) => t('survey.report.help.eligibility', { code: e.code || '', months: e.maxTenureMonths })).join(' ')]);
  }
  const sampled = narrative ? (narrative.themes || []).filter((b) => b.sampled) : [];
  if (sampled.length > 0) {
    notes.push([t('survey.report.methodSampling'),
      sampled.map((b) => t('survey.report.sampledNote', { count: b.commenters, sample: b.evaluated })).join(' ')]);
  }
  if (m.usesManagerStatus) notes.push([t('survey.report.methodManager'), t('survey.report.methodManagerValue')]);
  return notes;
}

export default function MethodAndNextSteps({ data, narrative, t, canEdit, onEdit }) {
  return (
    <SectionShell id="s08" title={t('survey.report.section.s08')}>
      {narrative && (
        <>
          <SubHeading style={{ marginTop: 0 }}>
            {t('survey.report.responsibilities')}
            {canEdit && <EditButton t={t} onClick={() => onEdit('responsibilities')} testId="edit-responsibilities" />}
          </SubHeading>
          <Grid $cols="minmax(160px, 1.2fr) minmax(160px, 1.4fr) minmax(100px, 1fr) minmax(120px, 1fr)">
            <HeadCell>{t('survey.report.colArea')}</HeadCell>
            <HeadCell>{t('survey.report.colOwner')}</HeadCell>
            <HeadCell>{t('survey.report.colFrequency')}</HeadCell>
            <HeadCell>{t('survey.report.colReportsTo')}</HeadCell>
            {narrative.responsibilities.map((r, i) => (
              <React.Fragment key={i}>
                <Cell $bold>{r.area}</Cell>
                <Cell>{r.owner}</Cell>
                <Cell>{r.frequency}</Cell>
                <Cell>{r.reportsTo}</Cell>
              </React.Fragment>
            ))}
          </Grid>

          <SubHeading>
            {t('survey.report.nextSteps')}
            {canEdit && <EditButton t={t} onClick={() => onEdit('nextSteps')} testId="edit-nextSteps" />}
          </SubHeading>
          <Steps>
            {narrative.nextSteps.map((s, i) => (
              <div key={i}><b>{String(i + 1).padStart(2, '0')}</b><strong>{s.title}</strong>{s.description}</div>
            ))}
          </Steps>
        </>
      )}

      <SubHeading>{t('survey.report.method')}</SubHeading>
      <Notes>
        {methodNotes(data, narrative, t).map(([label, value]) => (
          <div key={label}><span>{label}</span>{value}</div>
        ))}
      </Notes>
    </SectionShell>
  );
}
